import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import {
	ContactResponse,
	GetContactsApiResponse,
	GetAllContactsApiResponse
} from "../../DataModels/DataModels";
import { API_HTTP, resumeAnalyzerApiEndpoints } from "./endpoints";
import { AuthContext } from "../../context/AuthProvider";
import { User } from "firebase/auth";
import ApiInterface from "./api-interface";

const ONE_HOUR_DURATION_IN_MS = 60 * 60 * 1000;

export interface UseContactsResult {
	contacts: ContactResponse[];
	isLoading: boolean;
	isError: boolean;
}

export function useContacts(
	jobId: string,
	enabled: boolean = true
): UseContactsResult {
	const authContext = useContext(AuthContext);
	const user = authContext?.user as User | undefined;
	const userId = user?.uid;

	const { data, isLoading, isError } = useQuery<GetContactsApiResponse>({
		queryKey: ["contacts", jobId],
		queryFn: async () => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			return apiClient.get(resumeAnalyzerApiEndpoints.jobContacts, {
				jobId
			});
		},
		staleTime: ONE_HOUR_DURATION_IN_MS,
		enabled: !!userId && enabled
	});

	return {
		contacts: data?.contacts ?? [],
		isLoading,
		isError
	};
}

export function useAllContacts(): UseContactsResult {
	const authContext = useContext(AuthContext);
	const user = authContext?.user as User | undefined;
	const userId = user?.uid;

	const { data, isLoading, isError } = useQuery<GetAllContactsApiResponse>({
		queryKey: ["user", userId, "contacts"],
		queryFn: async () => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			return apiClient.get(resumeAnalyzerApiEndpoints.userContacts);
		},
		staleTime: ONE_HOUR_DURATION_IN_MS,
		enabled: !!userId
	});

	return {
		contacts: data?.contacts ?? [],
		isLoading,
		isError
	};
}

export function useAddContact(jobId: string) {
	const authContext = useContext(AuthContext);
	const user = authContext?.user as User | undefined;
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: {
			contactName: string;
			contactRole?: string;
			contactEmail?: string;
			contactLinkedIn?: string;
		}): Promise<ContactResponse> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			return apiClient.post(resumeAnalyzerApiEndpoints.jobContacts, payload, {
				jobId
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["contacts", jobId] });
			queryClient.invalidateQueries({ queryKey: ["history", jobId] });
		}
	});
}

export function useRemoveContact(jobId: string) {
	const authContext = useContext(AuthContext);
	const user = authContext?.user as User | undefined;
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (contactId: number): Promise<void> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			return apiClient.delete(
				resumeAnalyzerApiEndpoints.jobContactDelete,
				{},
				{ jobId, contactId }
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["contacts", jobId] });
			queryClient.invalidateQueries({ queryKey: ["history", jobId] });
		}
	});
}
